import { useState, useCallback, useRef, useMemo } from 'react';
import type { Node, Edge } from '@xyflow/react';
import type { UnifiedDiagramData, ExtendedExportFormat, CodeEvidence, UnifiedDiagramNode, E2EFlowPath } from '@veodiagram/shared';
import { ReactFlowRenderer, ReactFlowRendererHandle } from './renderers/ReactFlowRenderer';
import { Phase3DiagramRenderer, isPhase3DiagramType, type Phase3DiagramType, type Phase3DiagramRendererHandle } from './renderers/Phase3DiagramRenderer';
// Issue #330: Import E2EFlowRenderer for E2E flow diagrams
import { E2EFlowRenderer, type E2EFlowRendererHandle } from './renderers/e2eFlow';
import { exportDiagram } from './renderers/shared/exportUtils';
import { DiagramInfoPanel } from './DiagramInfoPanel';
import { DiagramLegend } from './DiagramLegend';
import { EvidencePanel } from './EvidencePanel';
// Issue #281: Import EmptyDiagramState for diagrams with no detected patterns
import { EmptyDiagramState } from './EmptyDiagramState';

// Issue #282: Helper function to check if a node has valid evidence
function nodeHasEvidence(node: UnifiedDiagramNode): boolean {
  const evidence = node.data?.evidence as CodeEvidence[] | undefined;
  return Array.isArray(evidence) && evidence.length > 0;
}

// Issue #282: Filter nodes that have evidence (for AI-generated diagrams)
function filterNodesWithEvidence(data: UnifiedDiagramData): UnifiedDiagramData {
  // Only filter Phase 3 (AI-generated) diagram types
  const phase3Types = new Set(['systemContext']);

  const diagramType = data.metadata?.diagramType;
  if (!diagramType || !phase3Types.has(diagramType)) {
    // Not a Phase 3 diagram, return as-is
    return data;
  }

  // Filter nodes that have evidence
  const nodesWithEvidence = data.nodes.filter(nodeHasEvidence);

  // If no nodes have evidence, return empty nodes array
  // (EmptyDiagramState will be shown)
  if (nodesWithEvidence.length === 0) {
    return {
      ...data,
      nodes: [],
      edges: [],
    };
  }

  // Create a set of node IDs that have evidence
  const validNodeIds = new Set(nodesWithEvidence.map(n => n.id));

  // Filter edges to only include those connecting valid nodes
  const validEdges = data.edges.filter(
    edge => validNodeIds.has(edge.source) && validNodeIds.has(edge.target)
  );

  return {
    ...data,
    nodes: nodesWithEvidence,
    edges: validEdges,
  };
}

// Issue #175: Type for node with evidence data
// Issue #404: Extended with E2E step-specific data
interface NodeWithEvidence {
  id: string;
  label: string;
  type?: string;
  description?: string;
  evidence?: CodeEvidence[];
  // E2E-specific step data for showing full details
  e2eStepData?: {
    stepType: string;
    method?: string;
    endpoint?: string;
    operation?: string;
    table?: string;
    model?: string;
    databaseStepData?: Record<string, unknown>;
    apiCallStepData?: Record<string, unknown>;
    apiRouteStepData?: Record<string, unknown>;
    backendLogicStepData?: Record<string, unknown>;
    sourceFile?: string;
    sourceLine?: number;
  };
}

interface DiagramViewerProps {
  data: UnifiedDiagramData;
  className?: string;
  readOnly?: boolean;
  onDataChange?: (data: UnifiedDiagramData) => void;
  onNodeClick?: (node: Node) => void;
  onEdgeClick?: (edge: Edge) => void;
  onError?: (error: string) => void;
}

export function DiagramViewer({
  data,
  className = '',
  readOnly = false,
  onDataChange,
  onNodeClick,
  onEdgeClick,
  onError,
}: DiagramViewerProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [notification, setNotification] = useState<string | null>(null);
  const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(false);
  const [isLegendVisible, setIsLegendVisible] = useState(false);
  // Issue #175: Evidence panel state
  const [isEvidencePanelOpen, setIsEvidencePanelOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState<NodeWithEvidence | null>(null);
  const reactFlowRef = useRef<ReactFlowRendererHandle>(null);
  const phase3Ref = useRef<Phase3DiagramRendererHandle>(null);
  // Issue #330: Ref for E2E flow renderer
  const e2eFlowRef = useRef<E2EFlowRendererHandle>(null);

  // Detect if this is a Phase 3 diagram
  const phase3DiagramType = useMemo(() => {
    const diagramType = data.metadata?.diagramType;
    if (diagramType && isPhase3DiagramType(diagramType)) {
      return diagramType as Phase3DiagramType;
    }
    return null;
  }, [data.metadata?.diagramType]);

  // Issue #330: Detect if this is an E2E flow diagram
  const isE2EFlowDiagram = useMemo(() => {
    return data.metadata?.diagramType === 'e2eFlow';
  }, [data.metadata?.diagramType]);

  // Issue #330: Get E2E flow paths from metadata
  const e2eFlowData = useMemo(() => {
    if (!isE2EFlowDiagram) return null;
    const paths = (data.metadata as { e2eFlowPaths?: E2EFlowPath[] })?.e2eFlowPaths || [];
    return { paths };
  }, [isE2EFlowDiagram, data.metadata]);

  // Issue #282: Filter nodes without evidence for Phase 3 (AI-generated) diagrams
  // Nodes without evidence will be excluded, and EmptyDiagramState shown if all lack evidence
  const filteredData = useMemo(() => filterNodesWithEvidence(data), [data]);

  // Show notification
  const showNotification = useCallback((message: string) => {
    setNotification(message);
    setTimeout(() => setNotification(null), 3000);
  }, []);

  // Handle export
  const handleExport = useCallback(
    async (format: ExtendedExportFormat) => {
      setIsExporting(true);
      setExportMenuOpen(false);

      try {
        let element: HTMLElement | null = null;

        if (format === 'png' || format === 'svg') {
          // Issue #330: Use appropriate ref based on diagram type
          if (isE2EFlowDiagram) {
            element = e2eFlowRef.current?.getViewportElement() || null;
          } else if (phase3DiagramType) {
            element = phase3Ref.current?.getViewportElement() || null;
          } else {
            element = reactFlowRef.current?.getViewportElement() || null;
          }
        }

        await exportDiagram(data, element, format);
        showNotification(`Exported as ${format.toUpperCase()}`);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Export failed';
        onError?.(message);
        showNotification(`Export failed: ${message}`);
      } finally {
        setIsExporting(false);
      }
    },
    [data, onError, showNotification, isE2EFlowDiagram, phase3DiagramType]
  );

  // Issue #175, #179: Handle node click to show evidence panel
  // Supports both node-level evidence and filtered diagram-level evidence
  // Falls back to showing all diagram-level evidence if no specific match found
  const handleInternalNodeClick = useCallback((node: Node) => {
    // Extract evidence data from the React Flow node
    const nodeData = node.data as Record<string, unknown>;
    const nodeFilePath = nodeData.filePath as string | undefined;

    // Try node-level evidence first
    let evidence = nodeData.evidence as CodeEvidence[] | undefined;
    let evidenceSource: 'node' | 'filtered' | 'diagram' = 'node';

    // If no node-level evidence, try to filter diagram-level evidence by node's filePath
    if ((!evidence || evidence.length === 0) && nodeFilePath && data.metadata.sourceEvidence) {
      evidence = data.metadata.sourceEvidence.filter(e =>
        e.filePath === nodeFilePath ||
        e.filePath.endsWith(nodeFilePath) ||
        nodeFilePath.endsWith(e.filePath.split('/').pop() || '')
      );
      if (evidence.length > 0) {
        evidenceSource = 'filtered';
      }
    }

    // Fallback: If still no evidence, show ALL diagram-level evidence
    if ((!evidence || evidence.length === 0) && data.metadata.sourceEvidence && data.metadata.sourceEvidence.length > 0) {
      evidence = data.metadata.sourceEvidence;
      evidenceSource = 'diagram';
    }

    // Issue #404: Extract E2E step-specific data for showing full details
    let e2eStepData: NodeWithEvidence['e2eStepData'] = undefined;
    const stepType = nodeData.type as string | undefined;
    if (stepType && typeof stepType === 'string') {
      e2eStepData = {
        stepType,
        method: nodeData.method as string | undefined,
        endpoint: nodeData.endpoint as string | undefined,
        operation: nodeData.operation as string | undefined,
        table: nodeData.table as string | undefined,
        model: nodeData.model as string | undefined,
        databaseStepData: nodeData.databaseStepData as Record<string, unknown> | undefined,
        apiCallStepData: nodeData.apiCallStepData as Record<string, unknown> | undefined,
        apiRouteStepData: nodeData.apiRouteStepData as Record<string, unknown> | undefined,
        backendLogicStepData: nodeData.backendLogicStepData as Record<string, unknown> | undefined,
        sourceFile: nodeData.sourceFile as string | undefined,
        sourceLine: nodeData.sourceLine as number | undefined,
      };
    }

    const nodeWithEvidence: NodeWithEvidence = {
      id: node.id,
      label: (nodeData.label as string) || node.id,
      type: node.type,
      description: evidenceSource === 'diagram'
        ? `${(nodeData.description as string) || ''}\n\nShowing all diagram evidence (no specific match for this node)`
        : nodeData.description as string | undefined,
      evidence: evidence && evidence.length > 0 ? evidence : undefined,
      e2eStepData,
    };

    // Set selected node and open evidence panel
    setSelectedNode(nodeWithEvidence);
    setIsEvidencePanelOpen(true);

    // Also call the external handler if provided
    onNodeClick?.(node);
  }, [onNodeClick, data.metadata.sourceEvidence]);

  // Issue #179: Handle viewing all diagram-level evidence
  const handleViewAllEvidence = useCallback(() => {
    const allEvidence = data.metadata.sourceEvidence;
    if (allEvidence && allEvidence.length > 0) {
      setSelectedNode({
        id: 'diagram-all-evidence',
        label: data.metadata.title,
        type: 'diagram',
        description: `All code evidence for this ${data.metadata.diagramType || 'diagram'}`,
        evidence: allEvidence,
      });
      setIsEvidencePanelOpen(true);
    }
  }, [data.metadata]);

  return (
    <div className={`flex flex-col h-full bg-white dark:bg-slate-900 ${className}`}>
      {/* Header with title and export */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
        {/* Title */}
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
            {data.metadata.title}
          </h2>
          {data.metadata.c4Level && (
            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded">
              C4 {data.metadata.c4Level}
            </span>
          )}
          {data.metadata.diagramType && (
            <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded">
              {data.metadata.diagramType}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Info Button */}
          <button
            onClick={() => setIsInfoPanelOpen(true)}
            className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            title="About this diagram (i)"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>

          {/* Legend Toggle */}
          <button
            onClick={() => setIsLegendVisible(!isLegendVisible)}
            className={`p-2 rounded-lg transition-colors ${
              isLegendVisible
                ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30'
                : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
            title="Toggle legend"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </button>

          {/* Issue #179: View All Evidence Button */}
          {data.metadata.sourceEvidence && data.metadata.sourceEvidence.length > 0 && (
            <button
              onClick={handleViewAllEvidence}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
              title="View all code evidence"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
              </svg>
              <span className="hidden sm:inline">Evidence</span>
              <span className="px-1.5 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-full">
                {data.metadata.sourceEvidence.length}
              </span>
            </button>
          )}

          {/* Export Dropdown */}
          <div className="relative">
            <button
              onClick={() => setExportMenuOpen(!exportMenuOpen)}
              disabled={isExporting}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
            >
              {isExporting ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Exporting...
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Export
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </>
              )}
            </button>

            {exportMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-1 z-50">
                <button
                  onClick={() => handleExport('png')}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                >
                  <span className="w-6 text-center">🖼️</span>
                  Export as PNG
                </button>
                <button
                  onClick={() => handleExport('svg')}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                >
                  <span className="w-6 text-center">📐</span>
                  Export as SVG
                </button>
                <button
                  onClick={() => handleExport('json')}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center gap-2"
                >
                  <span className="w-6 text-center">📄</span>
                  Export as JSON
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      {data.metadata.description && (
        <div className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
          {data.metadata.description}
        </div>
      )}

      {/* Renderer */}
      <div className="flex-1 relative">
        {/* Issue #281/#282: Show EmptyDiagramState when detection fails or all nodes lack evidence */}
        {filteredData.nodes.length === 0 && !isE2EFlowDiagram ? (
          <EmptyDiagramState
            diagramType={filteredData.metadata?.diagramType}
            className="h-full"
          />
        ) : (
          // Issue #330: Use E2EFlowRenderer for E2E flow diagrams
          isE2EFlowDiagram && e2eFlowData ? (
            <E2EFlowRenderer
              ref={e2eFlowRef}
              data={e2eFlowData}
              readOnly={readOnly}
              onNodeClick={handleInternalNodeClick}
              onEdgeClick={onEdgeClick}
              className="h-full"
            />
          ) : phase3DiagramType ? (
            <Phase3DiagramRenderer
              ref={phase3Ref}
              data={filteredData}
              diagramType={phase3DiagramType}
              readOnly={readOnly}
              onNodeClick={handleInternalNodeClick}
              onEdgeClick={onEdgeClick}
              className="h-full"
            />
          ) : (
            <ReactFlowRenderer
              ref={reactFlowRef}
              data={filteredData}
              readOnly={readOnly}
              onDataChange={onDataChange}
              onNodeClick={handleInternalNodeClick}
              onEdgeClick={onEdgeClick}
              className="h-full"
            />
          )
        )}

        {/* Close export menu when clicking outside */}
        {exportMenuOpen && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setExportMenuOpen(false)}
          />
        )}

        {/* Notification Toast */}
        {notification && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 px-4 py-2 bg-slate-800 text-white text-sm rounded-lg shadow-lg z-50">
            {notification}
          </div>
        )}

        {/* Legend Overlay */}
        <DiagramLegend
          diagramType={data.metadata?.diagramType}
          isVisible={isLegendVisible}
          onClose={() => setIsLegendVisible(false)}
          position="bottom-right"
        />
      </div>

      {/* Info Panel */}
      <DiagramInfoPanel
        diagramType={data.metadata?.diagramType}
        isOpen={isInfoPanelOpen}
        onClose={() => setIsInfoPanelOpen(false)}
        nodeCount={data.nodes.length}
        edgeCount={data.edges.length}
      />

      {/* Issue #175: Evidence Panel */}
      <EvidencePanel
        node={selectedNode}
        isOpen={isEvidencePanelOpen}
        onClose={() => {
          setIsEvidencePanelOpen(false);
          setSelectedNode(null);
        }}
      />
    </div>
  );
}

export default DiagramViewer;
